"use client";
import React, { useState } from "react";
import AppContainer from "../appContainer";
import { CustomButton, FlexBetween, FlexCenterCol, FlexCol } from "../ui";
import {
  BdAltArrowLeft,
  BdAltArrowRight,
  BdCloseCircle,
} from "solar-icon-react/bd";
import { cn, faToEnDigits } from "@/lib";
import Table from "../ui/dropdown/table/table";
import Image from "next/image";
import ReactPaginate from "react-paginate";
import { useGetWalletTransactions } from "@/hooks";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { getWalletTransactionsAdapter } from "@/lib/adapters/getWalletTransactionsAdapter";
import { WalletTransactionsTableHeaders } from "@/constants";
import { routes } from "@/routes";
import { useTranslations } from "next-intl";
import Link from "next/link";

import CustomDatePicker from "@/components/ui/datePicker";
import { DateObject } from "react-multi-date-picker";

const RecentTransactionsSection = () => {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentPage = Number(searchParams?.get("page") || "1");
  const fromDateParams = searchParams?.get("from-date") ?? null;
  const toDateParams = searchParams?.get("to-date") ?? null;
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  const [showRemoveFilters, setShowRemoveFilters] = useState(
    !!(fromDateParams || toDateParams),
  );

  const { data: walletTransactions } = useGetWalletTransactions();

  const currentLimit = Number(searchParams?.get("limit")) || 6;

  const params = new URLSearchParams(searchParams?.toString() || "");

  const resetFilters = () => {
    setShowRemoveFilters(false);
    setFromDate("");
    setToDate("");
    params.delete("from-date");
    params.delete("to-date");
    router.push(`${pathname}?${params.toString()}`);
  };

  const handleDateChange = (date: DateObject, key: "fromDate" | "toDate") => {
    if (key === "fromDate") {
      const formattedFromDate = faToEnDigits(
        date?.format("YYYY/MM/DD")?.replace(/-/g, "/"),
      );
      setFromDate(formattedFromDate);
      params.set("from-date", formattedFromDate);
    }

    if (key === "toDate") {
      const formattedToDate = faToEnDigits(
        date?.format("YYYY/MM/DD")?.replace(/-/g, "/"),
      );
      setToDate(formattedToDate);
      params.set("to-date", formattedToDate);
    }

    setShowRemoveFilters(true);

    const newUrl = `${pathname}?${params.toString()}`.replaceAll("%2F", "/");
    router.push(newUrl, { scroll: false });
  };

  const total = walletTransactions?.total ?? 0;
  const adaptedWalletTransactions = getWalletTransactionsAdapter(
    walletTransactions?.data ?? [],
  );
  const pageCount = total ? Math.ceil(total / currentLimit) : 0;

  const handlePageClick = (event: { selected: number }) => {
    const params = new URLSearchParams(searchParams?.toString() ?? "");
    params.set("page", String(event.selected + 1));
    params.set("limit", String(currentLimit));
    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  };
  const nextControlPaginationClass = cn(
    "cursor-pointer hover:text-primary-main transition-colors",
    currentPage == pageCount && "pointer-events-none! opacity-50",
  );
  const previousControlPaginationClass = cn(
    "cursor-pointer hover:text-primary-main transition-colors",
    currentPage === 1 && "pointer-events-none! opacity-50",
  );
  return (
    <section className="mb-20 w-full">
      <AppContainer className="h-auto">
        <div className="from-primary-main relative mx-auto size-full max-w-[700px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:max-w-[900px] xl:max-w-full">
          <FlexCol className="overflow-hidden rounded-2xl bg-white px-4 py-8 sm:px-8 lg:px-16 xl:px-20">
            <FlexBetween className="relative mb-8 flex-wrap gap-6 xl:gap-0">
              <p
                className={cn(
                  "text-sm font-bold xl:text-lg",
                  !adaptedWalletTransactions &&
                    "w-full border-b-2 border-b-gray-200 pb-6 text-center",
                )}
              >
                {t("walletTransactions.recent")}
              </p>
              {adaptedWalletTransactions && (
                <FlexBetween
                  gap={8}
                  className="mx-auto text-xs sm:gap-10 lg:text-sm"
                >
                  <FlexBetween
                    gap={4}
                    className="flex-wrap text-center lg:text-right xl:gap-8"
                  >
                    <p className="text-custom-gray-200 mx-auto">
                      {t("walletTransactions.fromDate")}
                    </p>
                    <FlexBetween className="grow sm:gap-4" gap={2}>
                      <CustomDatePicker
                        placeholder={t("walletTransactions.fromDate")}
                        hasShadow
                        value={fromDate}
                        format="YYYY/MM/DD"
                        onChange={(value) =>
                          handleDateChange(value, "fromDate")
                        }
                      />
                    </FlexBetween>
                  </FlexBetween>
                  <FlexBetween
                    gap={4}
                    className="flex-wrap text-center lg:text-right xl:gap-8"
                  >
                    <p className="text-custom-gray-200 mx-auto">
                      {t("walletTransactions.toDate")}
                    </p>
                    <FlexBetween className="grow sm:gap-4" gap={2}>
                      <CustomDatePicker
                        placeholder={t("walletTransactions.toDate")}
                        hasShadow
                        value={toDate}
                        format="YYYY/MM/DD"
                        onChange={(value) => handleDateChange(value, "toDate")}
                      />
                    </FlexBetween>
                    {showRemoveFilters && (
                      <FlexBetween
                        gap={2}
                        className="text-primary-main absolute top-0 left-0 cursor-pointer text-xs sm:text-sm xl:static"
                        onClick={resetFilters}
                      >
                        <p>{t("walletTransactions.removeFilters")}</p>
                        <span className="h-4 w-4 sm:h-5 sm:w-5">
                          <BdCloseCircle className="text-primary-main size-full!" />
                        </span>
                      </FlexBetween>
                    )}
                  </FlexBetween>
                </FlexBetween>
              )}
            </FlexBetween>
            {!adaptedWalletTransactions ||
            adaptedWalletTransactions.length === 0 ? (
              <FlexCenterCol gap={6} className="size-full grow">
                <figure className={cn("basis-1/2")}>
                  <Image
                    src="/svg/no-data-amico.svg"
                    width={0}
                    height={0}
                    sizes="100vw"
                    className={cn("size-full h-60 object-contain")}
                    alt={t("walletTransactions.noDataAlt")}
                  />
                </figure>
                <p className="text-xl font-bold">
                  {t("walletTransactions.noDataMessage")}
                </p>
                <Link href={routes.main.SERVICES.primaryPath} className="mt-4">
                  <CustomButton
                    className={cn("bg-primary-main! mx-auto w-max px-4!")}
                    iconPosition="end"
                    icon={<BdAltArrowLeft size={25} />}
                  >
                    {t("services.servicesPage")}
                  </CustomButton>
                </Link>
              </FlexCenterCol>
            ) : (
              <>
                <Table
                  headers={WalletTransactionsTableHeaders}
                  data={adaptedWalletTransactions}
                  className="dir-ltr mb-8 text-sm xl:text-base"
                />
                <div className="noselect dir-ltr mr-auto w-max text-sm!">
                  <ReactPaginate
                    breakLabel="..."
                    forcePage={currentPage - 1}
                    nextLabel={
                      <div className={cn("flex items-center gap-2")}>
                        <span>{t("pagination.next")}</span>
                        <BdAltArrowRight size={28} className="text-inherit" />
                      </div>
                    }
                    previousLabel={
                      <div className={cn("flex items-center gap-2")}>
                        <BdAltArrowLeft size={28} className="text-inherit" />
                        <span>{t("pagination.previous")}</span>
                      </div>
                    }
                    onPageChange={handlePageClick}
                    pageCount={pageCount}
                    renderOnZeroPageCount={null}
                    className={cn(
                      "text-custom-gray-200 flex items-center gap-5",
                    )}
                    pageClassName="cursor-pointer"
                    nextClassName={nextControlPaginationClass}
                    previousClassName={previousControlPaginationClass}
                    activeClassName="text-primary-main"
                    pageRangeDisplayed={3}
                    marginPagesDisplayed={1}
                  />
                </div>
              </>
            )}
          </FlexCol>
        </div>
      </AppContainer>
    </section>
  );
};

export default RecentTransactionsSection;
